import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

import { useConfig } from "./ConfigProvider";
import type { SiteConfig } from "./schema";

export type ThemeMode = "light" | "dark" | "system";

const STORAGE_KEY = "site-theme";

const ThemeContext = createContext<{
  mode: ThemeMode;
  resolved: "light" | "dark";
  setMode: (mode: ThemeMode) => void;
}>({ mode: "system", resolved: "light", setMode: () => {} });

/** Runs before hydration so the correct theme class is on <html> immediately. */
export const themeBootstrapScript = `(function(){try{var m=localStorage.getItem('${STORAGE_KEY}')||'system';var d=m==='dark'||(m==='system'&&window.matchMedia('(prefers-color-scheme: dark)').matches);document.documentElement.classList.toggle('dark',d);document.documentElement.style.colorScheme=d?'dark':'light';}catch(e){}})();`;

function systemPrefersDark() {
  return typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>("system");
  const [resolved, setResolved] = useState<"light" | "dark">("light");

  useEffect(() => {
    const stored = (localStorage.getItem(STORAGE_KEY) as ThemeMode | null) ?? "system";
    setModeState(stored);
  }, []);

  useEffect(() => {
    const apply = () => {
      const dark = mode === "dark" || (mode === "system" && systemPrefersDark());
      document.documentElement.classList.toggle("dark", dark);
      document.documentElement.style.colorScheme = dark ? "dark" : "light";
      setResolved(dark ? "dark" : "light");
    };
    apply();
    if (mode !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, [mode]);

  const setMode = (next: ThemeMode) => {
    setModeState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* storage unavailable — theme is session-only */
    }
  };

  return (
    <ThemeContext.Provider value={{ mode, resolved, setMode }}>{children}</ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}

function paletteVars(palette: SiteConfig["theme"]["light"]) {
  return [
    `--background:${palette.background}`,
    `--foreground:${palette.foreground}`,
    `--card:${palette.surface0}`,
    `--card-foreground:${palette.foreground}`,
    `--popover:${palette.surface0}`,
    `--popover-foreground:${palette.foreground}`,
    `--primary:${palette.primary}`,
    `--primary-foreground:${palette.primaryForeground}`,
    `--secondary:${palette.surface2}`,
    `--secondary-foreground:${palette.foreground}`,
    `--muted:${palette.surface1}`,
    `--muted-foreground:${palette.muted}`,
    `--accent:${palette.accent}`,
    `--accent-foreground:${palette.accentForeground}`,
    `--border:${palette.border}`,
    `--input:${palette.border}`,
    `--ring:${palette.accent}`,
    `--surface-0:${palette.surface0}`,
    `--surface-1:${palette.surface1}`,
    `--surface-2:${palette.surface2}`,
    `--surface-3:${palette.surface3}`,
    `--surface-4:${palette.surface4}`,
    `--on-dark-fold:${palette.onDarkFold}`,
  ].join(";");
}

/** Injects the JSON palette as CSS custom properties. Rendered during SSR too,
 *  so there is no unstyled flash before hydration. */
export function ThemeStyle() {
  const { theme } = useConfig();
  const css = `:root{${paletteVars(theme.light)};--radius:${theme.radius};--font-heading:${theme.fonts.heading};--font-body:${theme.fonts.body}}
.dark{${paletteVars(theme.dark)}}`;
  return <style dangerouslySetInnerHTML={{ __html: css }} />;
}
